/* The Course panel: her modules, and the blocks each module is taught from.
 *
 * The same shape as website.jsx, deliberately — a module and a page are both a
 * record with typed blocks under it, and both screens follow the pattern in
 * CLAUDE.md. The field components they share are in fields.jsx.
 */
import { useState } from "react";
import { applyCourseBlock, buildCourseContext, buildModuleContext,
  parseCourseBlock, parseModuleBlock, stripCourseBlock } from "../../utils/courseBlock";
import { courseSpecLines } from "../../utils/courseBlockKinds";
import { buildMomentumContext } from "../../utils/momentumContext";
import { offerLine } from "../../utils/offerLine";
import { COURSE_BLOCK_KINDS, MODULE_STATUS_LABEL, blockContent, blockKind, blockMinutes, blockWords, blocksOf, missingBlocks, moduleMinutes, moduleStatus, moduleWords, withBlockContent } from "../../utils/courseBlockKinds";
import { SectionStatus } from "./fields";
import { Block } from "./block";
import { Drafter } from "./drafter";
import { useRecordKeep } from "../../utils/pageChat";
import { B, STATUS_DOT, btnGhost, nb, pDesc, pTitle, pTitleAlone } from "./tokens";
import { AddButton, Back, Badge, Bread, ChatWithBrill, ContextChips, Menu2, Note, PageTitleField, SavedTick, StateDots, Status, useTwoPane } from "./ui";
/* The bar, the rail and the footer are the same on every panel — shell.jsx. */
import { BarBack, L1Table, MomentumBar, RecordCopy, RecordFooter } from "./shell";
import { Lock } from "lucide-react";

/* LEADS, NOT REPLACES. Her words, 2026-09-24: "offer takes priority but still
   look at the offer in the accelerator — in the case of a mini course I might do
   a mini AI training course. it could be helpful to have it relatively
   unrelated but still threaded." So the linked offer and the outcome set WHAT is
   taught, and her Accelerator work is the thread that makes it recognisably
   hers. Exported for courseOfferContext.test.js, which pins that it is sent. */
export const COURSE_OFFER_LEADS = "Teach THIS offer, to this module's outcome — that is what the course is about, and where they pull a different way from her Accelerator work, the outcome wins. But keep it threaded to her Signature Offer, Method and Curriculum: her client, her language, and how this connects to her bigger work, even when the topic is fairly unrelated. Thread it; do not teach or sell the Signature Offer here unless it is the same offer.";

export /* ONE call to Brill for one block of a module, shared by the sparkle and the
   drafting chat so the context is assembled once. Throws with a sentence the
   coach can read; both callers show it where she's looking.

   COST: one API call per invocation. Her locked work rides along on every
   message, scoped by CONTEXT_SCOPES.course_copy to what teaching a module needs
   — her method, her curriculum, her client and her voice — rather than the whole
   locked record. A chat is therefore dearer per result than a sparkle, because
   the conversation grows and re-sends with each turn.                          */
async function courseDraftReply({ token, module, kindId = null, chatContext = {}, messages, offer = null }) {
  const ctx = buildMomentumContext({
    moduleId: "course_copy",
    acceleratorOutputs: chatContext.acceleratorOutputs,
    activeNiche: chatContext.activeNiche,
    isStale: (id, name) => {
      const stamp = chatContext.outputStamps?.[id]?.[name];
      return !!(chatContext.activeNiche && stamp && stamp !== chatContext.activeNiche.id);
    },
  });
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      moduleId: "course_copy",
      max_tokens: 2000,   // model is chosen server-side; covers Sonnet 5's ~30% larger token counts
      moduleLabel: "Course module",
      archetype: chatContext.userSettings?.archetype || null,
      archetypePair: chatContext.userSettings?.archetypePair || null,
      messages: [
        ...(ctx ? [{ role: "user", content: ctx }] : []),
        /* Rebuilt per message from the module as it stands, so Brill edits what
           is on screen now rather than what was there when the chat opened.
           `kindId` is the SCOPE: one block beside its fields, or null for the
           whole module in the full-width chat. Same guide either way — the
           context line is what tells Brill which it is. */
        {
          role: "user",
          content: (kindId
            ? buildCourseContext({ module, kindId, specLines: courseSpecLines(kindId) })
            : buildModuleContext({ module }))
            /* THE OFFER THIS COURSE DELIVERS, and its description (offerLine). Sent
               nowhere before 2026-09-24: a course linked to an offer taught
               without knowing which one. COST: one line per message, only when
               the course has an offer. */
            /* And which of her offers LEADS. Her locked Method, Curriculum and
               Signature Offer ride above this in far more words, and on
               2026-09-24 a module whose outcome was "your wiring, your top three
               visibility strategies" came back teaching the flagship's fear and
               confidence work. Same fix as a website page (sections.jsx). */
            + (offer ? `\n\nOffer this course delivers: ${offerLine(offer)}\n${COURSE_OFFER_LEADS}` : ""),
        },
        ...messages,
      ],
    }),
  });
  const data = await res.json().catch(() => ({}));
  // 429 and 403 already carry a written explanation; anything else gets one.
  if (!res.ok) throw new Error(data?.error || "Something went wrong reaching Brill — try again in a moment. Nothing in this module was touched.");
  return data.content?.find((b) => b.type === "text")?.text || "";
}

/* A module with several blocks' content folded in. */
function withContents(module, contents = {}) {
  return Object.keys(contents).reduce((m, id) => withBlockContent(m, id, contents[id]), module);
}

/* A module that has these blocks — the ones it lacks join at the end, in the
   kinds' own order; the ones it has keep hers. Writing a block's words without
   this leaves them in a column no block shows. */
function withOrder(module, ids) {
  const order = Array.isArray(module?.blockOrder) ? [...module.blockOrder] : [];
  for (const kind of COURSE_BLOCK_KINDS) {
    if (ids.includes(kind.id) && !order.includes(kind.id)) order.push(kind.id);
  }
  return order.length === (module?.blockOrder || []).length ? module : { ...module, blockOrder: order };
}


/* ── Writing a module block with Brill ────────────────────────────────────────
   The shared Drafter (drafter.jsx) — the website's drafting screen, so a course
   block is written exactly the way a website section is: chat beside the
   fields, the split control, the lock card (rule 4), Saved on the fields, and
   the pager to move block to block (2026-09-16). What stays here is what only a
   course block knows: how to ask Brill (`courseDraftReply`), how to read her
   answer, and the chips for teaching. */
/* THE WAY BACK OUT OF THE DRAFTING SCREEN, named once. The bar renders it
   (`draftingBack` on CourseShell) and the Drafter still needs the words for
   Next's last step, which reads "Back to the module" off this same string. */
export const MODULE_BACK = "← Back to the module";


export function CourseDrafter({ module, kindId, chatContext = {}, offer = null, onSave, onBack, onGoToBlock, nextRecord = null, startScope = "block" }) {
  /* A module with no blocks yet (every new one) still opens: the block scope
     sits on the OUTCOME, the brief every other block is written from. */
  const kind = blockKind(kindId) || blockKind("outcome");
  /* THE CHAT IS KEPT, per module (supabase/0049). Nothing pruned: a module has
     a fixed set of kinds, and the block scope can open on one it does not have
     yet, so "not in this module" does not mean the chat is finished with. */
  const keep = useRecordKeep({ tool: "course", recordId: module?.id, blockKey: kind?.id });
  if (!module || !kind) return null;
  kindId = kind.id;

  // Every chip reflects real content, so the row can be trusted. See ContextChips.
  const has = (mod, name) => {
    const outputs = chatContext.acceleratorOutputs?.[mod] || {};
    return name ? !!outputs[name] : Object.keys(outputs).length > 0;
  };
  const contextItems = [
    { label: "Your voice", on: has("voice", "Voice Profile") },
    { label: "Your method", on: has("offer", "Your Method") },
    { label: "Your curriculum", on: has("offer", "Your Curriculum") },
    { label: "The offer it delivers", on: !!offer },
    /* The outcome is the BRIEF for every other block. */
    { label: "This module's outcome", on: !!String(module.outcome || "").trim() },
  ];
  const blocks = blocksOf(module);
  const index = blocks.findIndex((k) => k.id === kindId);

  return (
    <Drafter
      /* Recording the voice (supabase/0030): where a Lock lands. */
      voice={{ tool: "course_copy", recordType: "course_module", blockId: kindId, recordOf: (id) => ({ recordId: module.id, blockId: id }) }}
      keep={keep}
      libraryKinds={["story"]}
      kind={kind}
      content={blockContent(module, kindId)}
      onCommit={(content) => onSave(withOrder(withBlockContent(module, kindId, content), [kindId]))}
      ask={({ token, messages, content }) => courseDraftReply({
        token,
        // The module as it stands, with anything she's typed on this screen.
        module: withBlockContent(module, kindId, content),
        kindId, chatContext, messages, offer,
      })}
      parse={(reply, content) => {
        const block = parseCourseBlock(reply, kindId);
        return block ? applyCourseBlock(content, block, kindId) : null;
      }}
      strip={stripCourseBlock}
      opener={`I'm on the ${kind.label.toLowerCase()} for ${module.title || "this module"}. I can draft it from the outcome you've written, or you tell me the angle and I'll build to that. Which way?`}
      suggestions={{ first: ["Draft it from the outcome", "I'll tell you the angle"] }}
      contextItems={contextItems}
      pager={onGoToBlock && index >= 0 ? {
        items: blocks.map((k) => ({ id: k.id, label: k.label, written: blockWords(module, k.id) > 0 })),
        index,
        onGo: onGoToBlock,
        noun: "Block",
      } : null}
      chromeInBar
      backLabel={MODULE_BACK} onBack={onBack} nextRecord={nextRecord}
      where={module.title || "Untitled module"}
      startScope={startScope}
      /* THE WHOLE MODULE (decision 6: Courses' default door) — what the
         full-width module chat was, with its fields put back. One reply can
         fill several blocks, and ADD ones the module does not have yet; one
         Lock writes them all in ONE save, because a module's blocks are
         columns of the same row.
         COST: the same call the module chat made — one per message, with every
         block's labels and her existing words in the context. */
      record={{
        blockLabel: "Block",
        label: "Module",
        noun: "module",
        title: `Build ${module.title || "this module"}`,
        heading: module.title || "Untitled module",
        opener: `We're on ${module.title || "this module"}. Tell me what this session needs to do and I'll build it out — or say "draft the whole thing" and I'll take a first pass at every block from the outcome.`,
        suggestions: {
          first: ["Draft the whole thing from the outcome", "Just the teaching outline", "What's missing from this module?"],
          later: ["Shorter", "Make it sound more like me", "Give me another version"],
        },
        empty: "This module has no blocks yet. Whatever Brill writes adds its block here when you lock it in.",
        blocks: blocks.map((k) => ({ id: k.id, kind: k, content: blockContent(module, k.id) })),
        kindOf: blockKind,
        ask: ({ token, messages, contents }) => courseDraftReply({
          token, kindId: null, chatContext, messages, offer,
          module: withContents(module, contents),
        }),
        parse: (reply, contents) => {
          const parsed = parseModuleBlock(reply);
          if (!parsed) return null;
          const out = {};
          for (const id of Object.keys(parsed)) {
            if (!blockKind(id)) continue;
            out[id] = applyCourseBlock(contents[id] || blockContent(module, id), parsed[id], id);
          }
          return Object.keys(out).length ? out : null;
        },
        strip: stripCourseBlock,
        onCommitBlock: (id, content) => onSave(withBlockContent(module, id, content)),
        onCommitAll: (ready) => onSave(withOrder(withContents(module, ready), Object.keys(ready))),
      }}
    />
  );
}




/* WHAT SURVIVED THE TWO PARAGRAPHS. Courses carried about eighty words — a
   three-line description under the heading and a four-line Note under the
   table — explaining a screen she has used for weeks. Her call, 2026-09-15:
   "that is a lot of words, I'm not sure we need any of it, but can we
   consolidate whatever is most important and maybe just put it by a tooltip."

   Kept only what she cannot deduce by looking at the screen: the BOUNDARY (this
   holds words, not scheduling — so she does not go hunting for a date field),
   and what PULL FROM MY OFFER will not do. The second is the one that matters:
   a button that sounds like it might overwrite the module she has been writing.

   Dropped: "module by module", "what each one delivers, how the session runs" —
   the blocks on screen say that better than a sentence about them can. */
/* Assigned WITHOUT wrapping parens on purpose: `const NAME = (` is the shape
   src/hookOrder.test.js sweeps for, because an arrow-function COMPONENT written
   that way would slip past its hook-order check unseen. This is a JSX value, not
   a component — it is never rendered as <COURSE_HINT /> — but the sweep cannot
   know that, and adding it to the allowlist would spend the guard's strictness
   on a constant. Dropping two brackets is cheaper. */
const COURSE_HINT = <>Words only. What each module delivers and the order it runs in — scheduling,
    hosting and delivery happen wherever your course lives. Modules come from the
    curriculum you locked in My&nbsp;Offer, and <b>Pull from My Offer</b> adds any
    that aren't here yet; it never overwrites one you've started.</>;


/* The shell every Course view sits in: the title, the controls and the switch
   between them. Both views share it so the panel's own words are written once —
   the Website panel says its description twice, and they have already drifted. */
export function CourseShell({
  modules = [], error, onDismissError, notice, onDismissNotice,
  onAdd, canPull, onPull, seeded, onOpenChat, level1, drawer, children,
  onBackToCourses, courseName,
  /* {label, onClick} while she is drafting, and then it OWNS the lead slot —
     rule 9c, one way back per screen. The drafting screen's own row is gone
     (chromeInBar), so if this stops arriving there is no way back at all. */
  draftingBack = null,
  /* {label, badge} from utils/offerRung, or null. Handed in rather than looked
     up: this file knows about modules, not about the offer ladder. */
  rung = null,
}) {
  /* The bar keeps the way back only where it is the ONLY one. On a phone
     CourseCopy renders it instead, against whichever level is showing. */
  const twoPane = useTwoPane();
  /* While she is drafting, at ANY width (2026-09-24 — until then a phone
     only): the panel's chrome gives the screen up to the chat, and
     MomentumBar stands down too. The drafting screen's one row is all that
     sits above the conversation. */
  const bare = !!draftingBack;
  const counts = modules.reduce((acc, m) => {
    acc[moduleStatus(m)] = (acc[moduleStatus(m)] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      {/* BUILD · COURSES, then the course's own name big underneath — her shape,
          2026-09-15: "can it just be breadcrumbs at the top, build → sites →
          then my site is big." The panel's name has nowhere else to go now the
          heading belongs to the record. The paragraph that used to sit here,
          and the Note that used to sit under the table, are both in the hint. */}
      {/* THE PANEL'S OWN HEAD STANDS DOWN ON A PHONE WHILE SHE DRAFTS — her
          report, 2026-09-22. The crumb and the heading were two of the rows
          pushing the conversation down to four lines with its input below the
          fold, and neither answers a question she has while writing a block:
          the drafting screen's one row names where she is. */}
      {!bare && (<>
      <Bread text="Build" crumb="Courses" hint={COURSE_HINT} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h2 style={{ ...pTitleAlone, marginBottom: 0 }}>{courseName || "Courses"}</h2>
      {/* WHICH RUNG THIS SERVES, off the connected offer and never stored here
          (utils/offerRung). The ladder's short word, because a thing she MAKES
          is not a person: Outreach says Client where this says Core. */}
        {rung ? <Badge cls={rung.badge} text={rung.label} /> : null}
      </div>
      <div style={{ height: 24 }} />
      </>)}

      {/* Back, then which course, then Add — scope order, the same row Sites
          reads (shell.jsx MomentumBar). No toggle: the Table is the level above
          now, so going to it is leaving this course. */}
      <MomentumBar gap={14} drafting={!!draftingBack}
        lead={draftingBack
          ? <BarBack {...draftingBack} />
          : twoPane && onBackToCourses && <BarBack label="← All courses" onClick={onBackToCourses} />}
        level1={level1} drawer={drawer}
        add={<AddButton label="Add module" onClick={onAdd} />}
        extras={<>
          {/* The automatic pull is once-ever, which leaves it unreachable
              afterwards — nothing brings the modules back if she deletes them,
              and nothing retries for anyone whose flag was set before this
              existed. This is that route in. It adds only what ISN'T here, so
              pressing it twice does nothing the second time and it needs no
              confirm. */}
          {canPull && (
            <button onClick={onPull} style={{ ...btnGhost, padding: "10px 16px", fontSize: 13 }}>
              ⬇ Pull from My Offer
            </button>
          )}
          {/* "Open Course chat ↗" is gone (decision 9, closed with the shared
              drafting chat, 2026-09-16). It LEFT Momentum for the Accelerator's
              Course chat, which cannot write into these modules. Writing a
              module with Brill is "Write this module with Brill" on the module,
              and each block's chat icon. */}
        </>} />

      {modules.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
          {["done", "draft", "empty"].map((k) => (
            counts[k] ? (
              <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: B.mid, padding: "5px 12px", borderRadius: 20, background: B.surface, border: `1px solid ${B.border}` }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: B[STATUS_DOT[k]] }} />
                {counts[k]} {MODULE_STATUS_LABEL[k].toLowerCase()}
              </span>
            ) : null
          ))}
        </div>
      )}

      {notice && (
        <div onClick={onDismissNotice} style={{ background: B.tealQuiet, border: `1px solid ${B.tealLine}`, borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: B.text, cursor: "pointer" }}>
          {notice}
        </div>
      )}

      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger-text)", cursor: "pointer" }}>
          <b>Your modules didn't save.</b> {error} Nothing was lost — try again once you're back online.
        </div>
      )}

      {children}

    </>
  );
}


/* Grip, name, then the two numbers she said were the useful ones. */
export const COURSES_COLS = "28px 2fr 1.4fr .7fr 34px";

/* ── The landing screen: every course she teaches ───────────────────────────
   Was every MODULE of the one course. Her call, 2026-09-15, after the same
   change landed on Sites: "all of the sections should mirror this paradigm but
   of course the table columns are different", and then, asked directly whether
   the module table was worth keeping — "no I don't think the module table is
   particularly useful. They just want to know the name and if there are words,
   I think the TIME thing is helpful."

   So the module-level overview does not move, it GOES, and the two facts she
   named move into the rail's meta line (`CourseCopy` below) where they sit
   beside every module while she works instead of on a screen she has to leave
   the work to reach. What genuinely goes with it is the row of block pills —
   which of the five kinds a module has — because that will not fit 250px. The
   rail's dot still says whether a module is done. */
export function CoursesTable({
  courses = [], modulesOf, loading, error, onDismissError,
  onOpen, onDelete, onAdd, onReorder,
}) {
  return (
    <>
      {/* No crumb on the landing screen: its own heading already says Courses,
          and a breadcrumb repeating the title under it is an echo, not a trail.

          The text stays VISIBLE here and moves into the crumb's hint one level
          down — her split, and the right one: this is where someone arrives and
          needs orienting, and the module screen is where she is working and does
          not. Same words, one place each. */}
      <Bread text="Build" />
      <h2 style={pTitle}>Courses</h2>
      <p style={pDesc}>{COURSE_HINT}</p>

      {/* No view toggle: the thing this screen switches to is one course, and
          she picks WHICH by opening a row. See MomentumBar. */}
      <MomentumBar add={<AddButton label="Add course" onClick={() => onAdd?.()} />} />

      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger-text)", cursor: "pointer" }}>
          <b>Your courses didn't save.</b> {error} Nothing was lost — try again once you're back online.
        </div>
      )}

      <L1Table
        records={courses} cols={COURSES_COLS} labels={["Course", "Modules", "Words", ""]}
        loading={loading} loadingText="Loading your courses…"
        empty={<>No courses yet. Click <b style={nb}>Add course</b> to start one.</>}
        onOpen={onOpen} onDelete={onDelete} onReorder={onReorder}
        deleteTitle="Delete course"
        rowFor={(c) => {
          const mods = modulesOf?.(c.id) || [];
          const words = mods.reduce((n, m) => n + moduleWords(m), 0);
          return (
            <>
              <div style={{ minWidth: 0 }}>
                {/* Same weight as a site's name — the two landing tables have to
                    read as the same screen with different columns. */}
                <div style={{ fontSize: 15, fontWeight: 700, color: c.title ? B.text : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.3 }}>
                  {c.title || "Untitled course"}
                </div>
              </div>
              {/* A dot per module, the same three states and the same colours the
                  Sites table uses for its pages — so how far along something is
                  reads identically on both landing screens. */}
              <StateDots noun="module"
                states={mods.map((m) => moduleStatus(m))}
                titles={mods.map((m) => `${m.title || "Untitled module"} — ${MODULE_STATUS_LABEL[moduleStatus(m)]}`)} />
              <div style={{ fontSize: 12.5, color: words ? B.mid : B.ghost, fontVariantNumeric: "tabular-nums" }}>
                {words || "—"}
              </div>
            </>
          );
        }} />

      <Note><b style={nb}>Open a course to build its modules.</b> Drag by the grip to put them in your order.</Note>
    </>
  );
}


/* Module copy: the modules down the side, the one she's writing beside them.
   The same two-column shape as the website's Page copy, for the same reason —
   moving to the next module is the thing she does most, and going back out to a
   list to do it puts a screen between her and the work. */
export function CourseCopy({ modules = [], selectedId, onSelect, onReorder, drafting, onMark, onExitToCourses, children }) {
  /* The rail, the empty state, and the rule that writing one block takes the
     rail off screen are all `RecordCopy` now (shell.jsx). What stays here is
     what a MODULE's row reads: its position, because a course runs in order,
     and the same dot the table's Status column shows.

     Reordering is in the rail now, by grip (rule 10, spec R5). It used to be on
     the Table only, with a comment saying the rail "needs the room" — the room
     it needs is 16px, and going back out to the Table to move module 4 above
     module 3 is exactly the trip the rail exists to save. */
  return (
    <RecordCopy
      /* See website.jsx: on a phone the modules list carries "All courses" and
         the bar stands down, so there is one way back per screen. */
      onExit={onExitToCourses} exitLabel="All courses"
      records={modules} selectedId={selectedId} onSelect={onSelect} onReorder={onReorder} onMark={onMark}
      emptyHint={<>No modules yet. Click <b style={nb}>Add module</b> above to start one.</>}
      noun="module" bare={drafting}
      rowFor={(m, i) => {
        /* HER TWO NUMBERS, 2026-09-15: "they just want to know the name and if
           there are words, I think the TIME thing is helpful." They were columns
           on the modules table until that table became the list of COURSES; this
           is where they landed, and it is the better place — beside every module
           while she writes, rather than on a screen she has to leave the work to
           reach. Empty says "nothing here yet" rather than "0m · 0 words", which
           reads as a measurement of something. */
        const minutes = moduleMinutes(m);
        const words = moduleWords(m);
        const parts = [minutes ? `${minutes}m` : null, words ? `${words} words` : null].filter(Boolean);
        return {
          number: i + 1,
          title: m.title,
          meta: parts.length ? parts.join(" · ") : "Nothing written yet",
          dot: B[STATUS_DOT[moduleStatus(m)]],
          dotTitle: MODULE_STATUS_LABEL[moduleStatus(m)],
          markable: moduleStatus(m) !== "empty",
          done: moduleStatus(m) === "done",
        };
      }}>
      {children}
    </RecordCopy>
  );
}


/* One module. Its blocks are the five kinds, in her order — each one rendered
   from the same field components the website's typed sections use, so a field
   added to a kind appears here with no change. */
export function CourseModule({
  module, index = 0, onSave, onDelete, onAddBlock, onRemoveBlock, onOpenChat,
  onDraft, onSparkle, sparkleId, drafted, error, onBackToTable,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  /* The name is typed into local state and written on the way out, the way every
     other field in Momentum works.

     It used to call `set` on each keystroke, which meant a Supabase write PER
     CHARACTER, and the field read its value back from `module` — so the round
     trip raced her typing and characters landed on top of each other. A new
     module, where the whole name is typed at once, is where it showed worst.

     Safe as local state because this editor unmounts whenever the module can
     change underneath it: it is keyed by module id in the rail, and the module
     chat replaces it entirely rather than sitting beside it. */
  const [title, setTitle] = useState(module?.title || "");
  if (!module) return null;
  const blocks = blocksOf(module);
  const canAdd = missingBlocks(module);
  const state = moduleStatus(module);
  const minutes = moduleMinutes(module);
  const words = moduleWords(module);

  const set = (k, v) => onSave({ ...module, [k]: v });

  return (
    <>
      {/* What this module IS, before what it says: where it sits, where it's up
          to, how long it runs and how much of it is written. */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", fontWeight: 700, color: B.label }}>Module {index + 1}</span>
        {/* DRAFT / DONE AT THE TOP, her instruction 2026-09-15: "move draft and
            done to the top where it has yellow draft then module 1 — draft with
            a yellow outline or done green. Saved auto saves there but yellow
            until you mark it done."

            It was a read-out here and the control was in the footer, under every
            block on the module. So the one thing she sets was the furthest thing
            from where it is read, and this line said "Draft" in a tone that gave
            no hint it could be changed. Now it IS the control.

            An EMPTY module keeps the read-out. Marking a module with no words
            Done is a claim about nothing, and rule 11 is that done is counted,
            not claimed. */}
        {state === "empty" ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: B.mid }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: B[STATUS_DOT[state]] }} />
            {MODULE_STATUS_LABEL[state]}
          </span>
        ) : (
          <SectionStatus value={module.status === "done" ? "done" : "draft"} onChange={(status) => set("status", status)} />
        )}
        <span style={{ fontSize: 11.5, color: B.faint }}>{minutes ? `${minutes} min planned` : "No timings yet"}</span>
        <span style={{ fontSize: 11.5, color: B.faint }}>{words ? `${words} words` : "No copy yet"}</span>
        {/* The read-out rule 1 turns on, now that the footer is a Delete. It
            says nothing while everything is fine except "Saved", and goes amber
            and NAMES the reason when a write is blocked — which is the whole
            point of it, because a failed save and a good one look identical
            otherwise. */}
        <SavedTick problem={error ? "That didn't save — try again" : ""} />
        {/* The whole module with Brill, fields on screen — the same bubble a
            script, a sequence and a newsletter have (2026-09-17). */}
        <span style={{ marginLeft: "auto", display: "flex" }}>
          <ChatWithBrill label="Write this module with Brill" onClick={onOpenChat} />
        </span>
      </div>

      {/* The module names itself at the top, the way a page does — and like a
          page's, the name reads as a heading until she goes for it. `set` writes
          on every keystroke here, so there is nothing to commit on the way out;
          the field takes no onCommit. */}
      <div style={{ display: "flex", marginBottom: 22 }}>
        <PageTitleField value={title} onChange={setTitle} placeholder="Name this module" label="Module name"
          onCommit={() => { if (title !== (module.title || "")) set("title", title); }} />
      </div>

      {/* NO ARCHETYPE NOTE. It sat here as a teal strip — "How you should run it"
          — and she cut it entirely on 2026-08-18. Her archetype already reaches
          Brill on every call (api/chat.js appends it to the system prompt), so
          the strip was asking her to type out something the model already knows,
          in the one place on the screen that isn't part of the module.
          `archetype_note` stays on the table and in COURSE_MODULE_FIELDS so no
          row loses anything; nothing reads or writes it. */}

      {blocks.length === 0 ? (
        <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 26, textAlign: "center", color: B.dim, fontSize: 13, lineHeight: 1.6 }}>
          Nothing in this module yet. Click <b style={nb}>Add block</b> and pick what it needs — an outcome is the usual place to start.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {blocks.map((kind) => {
            /* A block Brill just wrote is remounted, so its draft state picks up
               the new words, and told to open so she can see them. The stamp is
               per module as well as per block, so coming back to a module later
               doesn't re-open a block she has since closed. */
            const stamp = drafted && drafted.moduleId === module.id && drafted.kindId === kind.id ? drafted.n : 0;
            return (
              <CourseBlock key={`${kind.id}:${stamp}`} kind={kind} module={module} startOpen={stamp > 0}
                onSave={onSave} onRemove={onRemoveBlock}
                onDraft={onDraft} onSparkle={onSparkle} sparkling={sparkleId === kind.id}
                canSparkle={kind.id !== "outcome" && !!String(module.outcome || "").trim()} />
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 14 }}>
        {canAdd.length > 0 && (
          <div style={{ position: "relative", display: "inline-block" }}>
            <button onClick={() => setMenuOpen(!menuOpen)} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>+ Add block</button>
            {menuOpen && (
              <Menu2 head="What does this module need?" items={canAdd.map((k) => [
                "📘", k.label, k.hint, false, () => { setMenuOpen(false); onAddBlock(module, k.id); },
              ])} />
            )}
          </div>
        )}
      </div>

      {/* ── Nothing to press, the same as a page ────────────────────────────
          Blocks have always saved as she leaves them, so this row reports that
          rather than explaining it, and Delete moves down here beside the way
          out. Done stays: it is the one thing on this screen she sets and
          nothing sets for her, and it is what the table's dots read. */}
      {/* Done and the saved read-out have both gone UP, to the header line where
          the state is read; the way back is in the bar above that. What is left
          down here is Delete, which is the one thing that belongs at the bottom
          of the thing it deletes. */}
      <RecordFooter tick={false} onDelete={() => onDelete(module)} />
    </>
  );
}


/* One block of a module — the shared Block (block.jsx), which is the website
   section's row: collapsed by default, open to READ, the pencil to edit, the
   same four actions on every row. Item 5, 2026-09-16, her call: "website is the
   most complete … mirror that … use that for everything."

   What changed for a course block, said once:
     · it no longer opens to fields — open shows the copy, the pencil edits;
     · an empty block starts closed like a website section, and says what to
       press when opened;
     · the first-line preview on a closed row went with the website's header,
       which shows the word count instead;
     · the sparkle is drawn on every row. It drafts FROM the outcome, so on the
       outcome itself, and on any block before there is an outcome, it is shown
       disabled and says why, rather than missing (rule 3).
   What only a course block has — its minutes — rides in the header badge. */
export function CourseBlock({ kind, module, startOpen, onSave, onRemove, onDraft, onSparkle, sparkling, canSparkle }) {
  const isOutcome = kind.id === "outcome";
  const sparkleLabel = isOutcome
    ? "The outcome is what Brill drafts the other blocks from — write it yourself, or in chat"
    : canSparkle
      ? `Draft ${kind.label.toLowerCase()} from this module's outcome`
      : "Write this module's outcome first — that's what Brill drafts from";

  return (
    <Block
      kind={kind} noun="block"
      content={blockContent(module, kind.id)}
      words={(content) => blockWords(withBlockContent(module, kind.id, content), kind.id)}
      onCommit={(content) => onSave(withBlockContent(module, kind.id, content))}
      /* Only the teaching outline is timed, and `blockMinutes` is what knows
         that — the badge asks it rather than testing the kind's id here. */
      badges={(content) => {
        const minutes = blockMinutes(withBlockContent(module, kind.id, content), kind.id);
        return minutes ? (
          <span style={{ flexShrink: 0, padding: "2px 8px", borderRadius: 4, background: B.tealQuiet, border: `1px solid ${B.tealLine}`, fontSize: 10.5, fontWeight: 700, color: B.teal }}>{minutes} min</span>
        ) : null;
      }}
      sparkle={{
        onClick: () => onSparkle?.(module, kind.id),
        busy: sparkling,
        disabled: isOutcome || !canSparkle,
        label: sparkleLabel,
      }}
      onChat={() => onDraft?.(kind.id)}
      /* CLEAR, not Remove: the words go, not just the row (spec section 4), and
         in the same write, so a block she removed can't come back with its old
         words. The question is asked by `askClearBlock` in MomentumHub. */
      onClear={() => onRemove(module, kind.id, withBlockContent(module, kind.id, {}))}
      clearLabel={`Clear ${kind.label.toLowerCase()}`}
      startOpen={startOpen}
    />
  );
}

